import React, { useEffect, useMemo, useState } from 'react';
import {
  IonBadge,
  IonContent,
  IonButton,
  IonButtons,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonPage,
  IonText,
  useIonToast,
} from '@ionic/react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { arrowBackOutline } from 'ionicons/icons';

import dayjs from 'dayjs';

import { GestionErroresAPI } from 'src/services/gestionErrores';
import Gestion from 'src/services/gestionMarcacion';
import { ERROR_UI, TIPOS_EN_ORDEN } from 'src/modules/gestionErrores/constantes';
import { GestionErroresDetalleItem } from 'src/modules/gestionErrores/types';

import {
  agregarLpGestionado,
  listarLpGestionados,
} from 'src/modules/gestionErrores/gestionLpStorage';

import ReemplazoRapidoModal from 'src/modules/gestionErrores/components/ReemplazoRapidoModal';
import CorregirMarcacionModal from 'src/modules/gestionMarcacionV2/components/CorregirMarcacionModal';
import SuperaModal from 'src/modules/gestionMarcacionV2/components/SuperaModal';
import { RegistroMarcacion } from 'src/modules/gestionMarcacionV2/hooks/useGestionMarcacionApi';
import GestionSinAsignacionSelectorModal from 'src/modules/gestionErrores/components/GestionSinAsignacionSelectorModal';
import AsignarLimpiezaProfundaModal from 'src/modules/gestionErrores/components/AsignarLimpiezaProfundaModal';

import Header from 'src/components/Header';

type Params = {
  psId: string;
};

const parseRangoFromSearch = (search: string): { desde?: string; hasta?: string } => {
  try {
    const params = new URLSearchParams(search || '');
    const desde = params.get('desde') || undefined;
    const hasta = params.get('hasta') || undefined;
    return { desde, hasta };
  } catch {
    return {};
  }
};

// Clave en sessionStorage para marcar gestión local (sin re-consultar backend al volver).
// Motivo: el HNP puede seguir existiendo hasta reproceso/procesamiento; aun así queremos reflejar "GESTIONADO".
const STORAGE_KEY_GESTION_MARCACION = 'gestion_errores:gestion_marcacion:ultimo';

type GestionMarcacionHecha = {
  tipo_error: 'SIN-ENTRADA' | 'SIN-SALIDA';
  ps_nombre: string;
  operario_legajo: number | null;
  fecha: string; // YYYY-MM-DD
};

const _fechaISOFromFechaHora = (fechaHora: string): string => {
  // Normaliza distintas serializaciones de backend a YYYY-MM-DD.
  // Casos esperados:
  // - SQL/ISO: "YYYY-MM-DD HH:mm:ss" / "YYYY-MM-DDTHH:mm:ss"
  // - HTTP-date: "Tue, 13 Jan 2026 00:00:00 GMT" (evitar el bug "Tue, 13 Ja")
  const txt = String(fechaHora || '').trim();
  if (!txt) return '';

  // Atajo: si ya arranca con YYYY-MM-DD, tomamos solo la fecha.
  const m = txt.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];

  const d = dayjs(txt);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const _extraerHorasDesdeDetalle = (detalle: string): { hentrada?: string; hsalida?: string } => {
  // "detalle" viene del backend con patrón: "Hentrada=HH:MM Hsalida=HH:MM ...".
  const txt = String(detalle || '');
  const mEnt = txt.match(/Hentrada=([0-9]{2}:[0-9]{2})/i);
  const mSal = txt.match(/Hsalida=([0-9]{2}:[0-9]{2})/i);
  return {
    hentrada: mEnt ? mEnt[1] : undefined,
    hsalida: mSal ? mSal[1] : undefined,
  };
};

const _extraerTipoHoraDesdeDetalle = (detalle: string): string | null => {
  // "detalle" viene con patrón: "... TipoHora=XX ..." (puede venir vacío).
  const txt = String(detalle || '');
  const m = txt.match(/TipoHora=([^\s]+)/i);
  const val = m ? String(m[1] || '').trim() : '';
  return val ? val : null;
};

const _toNumberOrNull = (value: any): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const txt = String(value ?? '').trim();
  if (!txt) return null;
  if (!/^[0-9]+$/.test(txt)) return null;
  const n = Number(txt);
  return Number.isFinite(n) ? n : null;
};

const _leerMarcaGestionMarcacion = (): GestionMarcacionHecha | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY_GESTION_MARCACION);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.tipo_error !== 'SIN-ENTRADA' && parsed.tipo_error !== 'SIN-SALIDA') return null;
    if (!parsed.ps_nombre || !parsed.fecha) return null;
    return parsed as GestionMarcacionHecha;
  } catch {
    return null;
  }
};

const _consumirMarcaGestionMarcacion = (): GestionMarcacionHecha | null => {
  const marca = _leerMarcaGestionMarcacion();
  try {
    sessionStorage.removeItem(STORAGE_KEY_GESTION_MARCACION);
  } catch {
    // No bloquear si el storage falla.
  }
  return marca;
};

const GestionErroresDetalle: React.FC = () => {
  const { psId } = useParams<Params>();
  const history = useHistory();
  const location = useLocation();
  const [present] = useIonToast();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<GestionErroresDetalleItem[]>([]);
  const [rangoTexto, setRangoTexto] = useState<string>('');
  const [rowGestionar, setRowGestionar] = useState<GestionErroresDetalleItem | null>(null);

  // Selector de opciones para SIN-ASIGNACION (RR / LP / OS placeholder)
  const [rowSinAsignacion, setRowSinAsignacion] = useState<GestionErroresDetalleItem | null>(null);
  // Modal de asignación a LP
  const [rowAsignarLP, setRowAsignarLP] = useState<GestionErroresDetalleItem | null>(null);

  // Ventana de corrección (la misma de la pestaña Pendientes) para SIN-ENTRADA / SIN-SALIDA
  const [registroMarcacion, setRegistroMarcacion] = useState<RegistroMarcacion | null>(null);
  // Gestión de SUPERA (misma ventana que la pestaña Pendientes)
  const [registroSupera, setRegistroSupera] = useState<RegistroMarcacion | null>(null);

  // Marcas locales (solo UI): registros gestionados en el celular, sin reconsultar backend.
  const [gestionadosLocal, setGestionadosLocal] = useState<Record<string, boolean>>({});
  // Marcas locales para SIN-ASIGNACION gestionadas vía LP (por referencia_id).
  const [gestionadosLpLocal, setGestionadosLpLocal] = useState<Record<string, boolean>>({});

  const psIdNum = Number(psId);

  const fetchData = async (desde?: string, hasta?: string) => {
    try {
      setLoading(true);
      const data = await GestionErroresAPI.detalle(psIdNum, desde, hasta);
      setItems(data.datos || []);
      const r = data.rango;
      setRangoTexto(
        `Rango efectivo: ${r.fecha_desde_efectiva} a ${r.fecha_hasta_efectiva}`
      );
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo cargar el detalle.';
      present({ message: String(msg), duration: 3500, color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { desde, hasta } = parseRangoFromSearch(location.search);
    // Si viene rango del resumen, usamos ese mismo (no pedimos fechas de nuevo).
    fetchData(desde, hasta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psId, location.search]);

  useEffect(() => {
    // Al volver desde Gestión Marcación, consumimos la última marca y reflejamos "GESTIONADO".
    // Nota: no reconsultamos backend; la desaparición del registro ocurre cuando el procesamiento lo elimine.
    const marca = _consumirMarcaGestionMarcacion();
    if (!marca) return;
    const key = `${marca.tipo_error}|${String(marca.ps_nombre).trim()}|${String(marca.operario_legajo ?? '')}|${marca.fecha}`;
    setGestionadosLocal((prev) => ({ ...prev, [key]: true }));
  }, [location.key]);

  useEffect(() => {
    // Al cargar (o volver a esta pantalla), sincronizamos marcas LP desde sessionStorage.
    // Esto permite que, al volver desde otras pantallas, el estado visual se mantenga.
    try {
      const marcas = listarLpGestionados();
      const mapa: Record<string, boolean> = {};
      for (const m of marcas) {
        if (!m?.referencia_id) continue;
        mapa[m.referencia_id] = true;
      }
      setGestionadosLpLocal(mapa);
    } catch {
      // No bloquear si el storage falla.
    }
  }, [location.key]);

  const gestionarMarcacion = async (row: GestionErroresDetalleItem) => {
    // Flujo similar a EMSYS: desde el error, llevar directo a la pantalla para cargar hora.
    // Fuente datos: /gestion_marcacion/listar_gestion (ya devuelve idMarcacion necesario).
    try {
      setLoading(true);

      const extraerDatosDesdeRespuesta = (response: any): { datos: any[]; error?: string } => {
        const datos = response?.data?.datos;
        const error = response?.data?.error;
        return {
          datos: Array.isArray(datos) ? datos : [],
          error: typeof error === 'string' && error ? error : undefined,
        };
      };

      const fechaISO = _fechaISOFromFechaHora(row.fecha_hora);
      if (!fechaISO) {
        present({ message: 'No se pudo determinar la fecha para gestionar.', duration: 3500, color: 'danger' });
        return;
      }

      const estado = row.tipo_error;
      if (estado !== 'SIN-ENTRADA' && estado !== 'SIN-SALIDA') {
        present({ message: 'Este tipo de error no admite gestión de marcación.', duration: 3500, color: 'warning' });
        return;
      }

      const { hentrada, hsalida } = _extraerHorasDesdeDetalle(row.detalle);
      const tipoHoraDesdeDetalle = _extraerTipoHoraDesdeDetalle(row.detalle);

      // Camino preferido: si el backend ya devolvió idMarcacion, no dependemos de listar_gestion.
      // Esto evita el "No hay gestiones" cuando hay inconsistencias de filtros/PS en ese endpoint.
      const idMarcacionDirecto = _toNumberOrNull((row as any)?.idMarcacion);
      if (idMarcacionDirecto != null) {
        const entradaId = _toNumberOrNull((row as any)?.entrada_id);
        const salidaId = _toNumberOrNull((row as any)?.salida_id);
        const elegido = {
          nombre: row.operario_nombre,
          nroLegajo: row.operario_legajo,
          fecha: fechaISO,
          entrada: hentrada || '',
          salida: hsalida || '',
          total: '',
          idMarcacion: idMarcacionDirecto,
          entrada_id: entradaId,
          salida_id: salidaId,
          tipoMarcacion: (row as any)?.tipoMarcacion ?? null,
          tipoHora: (row as any)?.tipoHora ?? tipoHoraDesdeDetalle,
          nombrePServicio: String(row.ps_nombre || '').trim(),
          psId: row.ps_id,
        };

        // UX: abrir modal tipo EMSYS (Hora / Tipo), sin navegar.
        setRegistroMarcacion({ ...elegido, estado } as RegistroMarcacion);
        return;
      }

      const filtrosConPs: any = {
        fechaInicio: fechaISO,
        fechaFin: fechaISO,
        operario: '',
        estado,
        excluir: [],
        // Filtro robusto por PS (numérico): evita mismatch por nombre/espacios.
        psId: row.ps_id,
        nombrePs: String(row.ps_nombre || '').trim(),
      };

      // 1) Intento principal: filtrar por nombre de PS.
      // Nota: el backend filtra con igualdad estricta (ps.NombrePServicio = '...'), puede fallar por espacios/normalización.
      let datos: any[] = [];
      let ultimoErrorListado: string | undefined;
      try {
        const response = await Gestion.GestionAPI(filtrosConPs);
        const extraido = extraerDatosDesdeRespuesta(response);
        datos = extraido.datos;
        ultimoErrorListado = extraido.error;
      } catch (e1: any) {
        const msg1 = String(e1?.response?.data?.error || e1?.message || '');
        // Si el backend responde "No hay gestiones", reintentamos sin nombrePs y filtramos acá.
        if (!msg1.toLowerCase().includes('no hay gestiones')) {
          throw e1;
        }
        ultimoErrorListado = msg1;
      }

      // 2) Fallback: si no hay datos, reintentar sin nombrePs.
      if (!Array.isArray(datos) || datos.length === 0) {
        const filtrosSinPs: any = {
          fechaInicio: fechaISO,
          fechaFin: fechaISO,
          operario: '',
          estado,
          excluir: [],
          psId: row.ps_id,
          // Omitimos nombrePs a propósito.
        };
        const response2 = await Gestion.GestionAPI(filtrosSinPs);
        const extraido2 = extraerDatosDesdeRespuesta(response2);
        datos = extraido2.datos;
        ultimoErrorListado = extraido2.error || ultimoErrorListado;
      }

      const legajoRow = row.operario_legajo;
      const candidatos = (Array.isArray(datos) ? datos : []).filter((x: any) => {
        try {
          const legajoOk = legajoRow == null ? true : String(x?.nroLegajo) === String(legajoRow);
          const estadoOk = String(x?.estado || '').trim().toUpperCase() === estado;
          // Nota: ya filtramos por psId en backend; no dependemos del match exacto por nombre.
          return legajoOk && estadoOk;
        } catch {
          return false;
        }
      });

      const elegido = candidatos.find((x: any) => {
        // Desambiguación por hora cuando hay múltiples HNP del mismo operario/fecha.
        const ent = String(x?.entrada || '').trim();
        const sal = String(x?.salida || '').trim();
        if (estado === 'SIN-SALIDA' && hentrada) return ent === hentrada;
        if (estado === 'SIN-ENTRADA' && hsalida) return sal === hsalida;
        return false;
      }) || candidatos[0];

      if (!elegido) {
        // Si el backend indicó explícitamente que no hay gestiones, lo mostramos tal cual.
        const msg = ultimoErrorListado && ultimoErrorListado.toLowerCase().includes('no hay gestiones')
          ? ultimoErrorListado
          : 'No se encontró la marcación para gestionar (probablemente ya fue procesada o no está en Gestión Marcación).';
        present({ message: msg, duration: 4500, color: 'danger' });
        return;
      }

      // UX: abrir modal tipo EMSYS (Hora / Tipo), sin navegar.
      setRegistroMarcacion({ ...elegido, estado } as RegistroMarcacion);
    } catch (e: any) {
      const msg = e?.response?.data?.error || 'No se pudo iniciar la gestión de marcación.';
      present({ message: String(msg), duration: 3500, color: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  // Marca un registro como gestionado en pantalla (sin reconsultar backend).
  const marcarGestionadoLocal = (tipo: string, psNombre: string, legajo: any, fechaISO: string) => {
    const key = `${tipo}|${String(psNombre || '').trim()}|${String(legajo ?? '')}|${fechaISO}`;
    setGestionadosLocal((prev) => ({ ...prev, [key]: true }));
  };

  const gestionarSupera = (row: GestionErroresDetalleItem) => {
    const fechaISO = _fechaISOFromFechaHora(row.fecha_hora);
    const { hentrada, hsalida } = _extraerHorasDesdeDetalle(row.detalle);
    setRegistroSupera({
      nombre: row.operario_nombre || '',
      nroLegajo: row.operario_legajo ?? '',
      fecha: fechaISO,
      entrada: hentrada || null,
      salida: hsalida || null,
      total: row.total ?? null,
      idMarcacion: 0,
      entrada_id: _toNumberOrNull(row.entrada_id) ?? undefined,
      salida_id: _toNumberOrNull(row.salida_id) ?? undefined,
      tipoMarcacion: '',
      tipoHora: row.tipoHora ?? _extraerTipoHoraDesdeDetalle(row.detalle) ?? '',
      estado: 'SUPERA',
      nombrePServicio: String(row.ps_nombre || '').trim(),
      psId: row.ps_id,
    });
  };

  const itemsOrdenados = useMemo(() => {
    const orderIndex: Record<string, number> = {};
    TIPOS_EN_ORDEN.forEach((t, idx) => {
      orderIndex[t] = idx;
    });

    const copy = [...items];
    // Ocultar tipos que no se gestionan en este detalle (evita confusión y ruido en UI).
    // SUPERA sí se muestra: se gestiona con la misma ventana que la pestaña Pendientes.
    const filtrados = copy.filter(
      (x) =>
        x?.tipo_error !== 'REPETIDA' &&
        x?.tipo_error !== 'ALERTA_TOLERANCIA'
    );
    const conGestionLocal = filtrados.map((x) => {
      const fechaISO = _fechaISOFromFechaHora(x.fecha_hora);
      const key = `${x.tipo_error}|${String(x.ps_nombre || '').trim()}|${String(x.operario_legajo ?? '')}|${fechaISO}`;

      // Marca local para SIN-ENTRADA/SIN-SALIDA/SUPERA.
      if ((x.tipo_error === 'SIN-ENTRADA' || x.tipo_error === 'SIN-SALIDA' || x.tipo_error === 'SUPERA') && gestionadosLocal[key]) {
        return {
          ...x,
          gestionado: true,
          // En estos casos, el usuario pidió que se vea solo "GESTIONADO".
          modo_gestion: null,
        } as GestionErroresDetalleItem;
      }

      // SIN-ASIGNACION gestionada vía LP (solo UI): se marca como gestionado y se muestra el modo.
      if (x.tipo_error === 'SIN-ASIGNACION' && gestionadosLpLocal[String(x.referencia_id || '').trim()]) {
        return {
          ...x,
          gestionado: true,
          modo_gestion: 'LP',
        } as GestionErroresDetalleItem;
      }

      return x;
    });

    conGestionLocal.sort((a, b) => {
      const oa = orderIndex[a.tipo_error] ?? 999;
      const ob = orderIndex[b.tipo_error] ?? 999;
      if (oa !== ob) return oa - ob;
      // Orden estable por fecha_hora (string ISO) desc
      return String(b.fecha_hora || '').localeCompare(String(a.fecha_hora || ''));
    });
    return conGestionLocal;
  }, [items, gestionadosLocal, gestionadosLpLocal]);

  return (
    <IonPage>
      {/* Header estándar: mantiene barra roja y botón de menú */}
      <Header
        titulo={`Detalle PS ${psId}`}
        botonesDerecha={[
          {
            icono: arrowBackOutline,
            action: () => history.push(`/gestion-errores${String(location.search || '')}`),
          },
        ]}
      />

      {/*
        Importante: este id debe coincidir con el contentId que se pasa al componente Menu
        en App.tsx (contentId="gestion-errores"). Si no coincide, el menú lateral no abre.
      */}
      <IonContent id="gestion-errores" fullscreen>
        <IonLoading isOpen={loading} message="Cargando..." />

        {rangoTexto ? (
          <IonList>
            <IonItem lines="none">
              <IonText color="medium">{rangoTexto}</IonText>
            </IonItem>
          </IonList>
        ) : null}

        <IonList>
          {itemsOrdenados.map((row, idx) => {
            const ui = ERROR_UI[row.tipo_error as any];
            const esSinAsignacion = row.tipo_error === 'SIN-ASIGNACION';
            // SUPERA se muestra igual que SIN-ENTRADA/SIN-SALIDA (nombre + horas).
            const esSupera = row.tipo_error === 'SUPERA';
            const esSinEntradaSalida = row.tipo_error === 'SIN-ENTRADA' || row.tipo_error === 'SIN-SALIDA' || esSupera;
            const estaGestionado = !!row.gestionado;
            const modoGestion = (row.modo_gestion || '').toString().trim();

            const fechaISO = _fechaISOFromFechaHora(row.fecha_hora);
            const { hentrada, hsalida } = _extraerHorasDesdeDetalle(row.detalle);
            return (
              <IonItem key={`${row.referencia_id}-${idx}`}>
                <IonLabel>
                  {/*
                    Para SIN-ASIGNACION / SIN-ENTRADA / SIN-SALIDA:
                    - Mostramos el nombre del operario como título (más útil).
                    - El tipo de error ya se ve abajo en el badge, así evitamos duplicar texto.
                  */}
                  {esSinAsignacion || esSinEntradaSalida ? (
                    <div style={{ fontWeight: 700 }}>{row.operario_nombre || 'Operario'}</div>
                  ) : (
                    <>
                      <div style={{ fontWeight: 700 }}>
                        {ui?.label ? ui.label : row.tipo_error}
                      </div>
                      <div style={{ fontWeight: 700 }}>{row.operario_nombre || 'Operario'}</div>
                    </>
                  )}

                  {/* Resumen compacto: solo legajo + fecha (sin hora) */}
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>
                    Legajo: {row.operario_legajo ?? '-'}{fechaISO ? ` | Fecha: ${fechaISO}` : ''}
                  </div>

                  {/* Horas: mostrar solo lo que exista (SIN-ENTRADA/SIN-SALIDA/SIN-ASIGNACION) */}
                  {esSinAsignacion || esSinEntradaSalida ? (
                    <div style={{ marginTop: 8, fontSize: 16, fontWeight: 700, color: '#000' }}>
                      {hentrada ? <div>Entrada: {hentrada}</div> : null}
                      {hsalida ? <div>Salida: {hsalida}</div> : null}
                    </div>
                  ) : null}

                  {/* Para otros tipos, dejamos el detalle completo (puede ser relevante) */}
                  {!(esSinAsignacion || esSinEntradaSalida) ? (
                    <div style={{ marginTop: 6, fontSize: 12, whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {row.detalle}
                    </div>
                  ) : null}

                  <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {ui?.color ? <IonBadge color={ui.color}>{row.tipo_error}</IonBadge> : null}
                    {(esSinAsignacion || esSinEntradaSalida) && estaGestionado ? (
                      <>
                        <IonBadge color="success">GESTIONADO</IonBadge>
                        {esSinAsignacion && modoGestion ? <IonBadge color="medium">{modoGestion}</IonBadge> : null}
                      </>
                    ) : null}
                  </div>
                </IonLabel>
                <IonButtons slot="end">
                  {esSinAsignacion && !estaGestionado ? (
                    <IonButton fill="outline" onClick={() => setRowSinAsignacion(row)}>
                      GESTIONAR
                    </IonButton>
                  ) : null}
                  {esSinEntradaSalida && !estaGestionado ? (
                    <IonButton fill="outline" onClick={() => (esSupera ? gestionarSupera(row) : gestionarMarcacion(row))}>
                      GESTIONAR
                    </IonButton>
                  ) : null}
                </IonButtons>
              </IonItem>
            );
          })}
        </IonList>

        {/* Selector de opciones para SIN-ASIGNACION */}
        <GestionSinAsignacionSelectorModal
          isOpen={!!rowSinAsignacion}
          onClose={() => setRowSinAsignacion(null)}
          onElegirReemplazoRapido={() => {
            // Flujo existente: abrir Reemplazo Rápido.
            setRowGestionar(rowSinAsignacion);
            setRowSinAsignacion(null);
          }}
          onElegirLimpiezaProfunda={() => {
            // Nuevo flujo: asignar el error a una planificación de LP.
            setRowAsignarLP(rowSinAsignacion);
            setRowSinAsignacion(null);
          }}
        />

        {/* Modal LP: asignar SIN-ASIGNACION a planificación */}
        <AsignarLimpiezaProfundaModal
          isOpen={!!rowAsignarLP}
          row={rowAsignarLP}
          onClose={() => setRowAsignarLP(null)}
          onAssigned={({ referencia_id, ps_id, fecha, operario_legajo }) => {
            // Guardar marca local (sessionStorage) para:
            // - mostrar "GESTIONADO" en el detalle
            // - descontar del contador en el resumen
            agregarLpGestionado({
              referencia_id,
              ps_id,
              fecha,
              operario_legajo,
            });
            setGestionadosLpLocal((prev) => ({ ...prev, [referencia_id]: true }));
          }}
        />

        <ReemplazoRapidoModal
          isOpen={!!rowGestionar}
          row={rowGestionar}
          onClose={() => setRowGestionar(null)}
          onSuccess={() => {
            const { desde, hasta } = parseRangoFromSearch(location.search);
            fetchData(desde, hasta);
          }}
        />

        <CorregirMarcacionModal
          open={!!registroMarcacion}
          registro={registroMarcacion}
          onClose={() => setRegistroMarcacion(null)}
          onSuccess={() => {
            // Marcamos como gestionado localmente (sin re-consultar backend).
            if (!registroMarcacion) return;
            marcarGestionadoLocal(
              registroMarcacion.estado,
              registroMarcacion.nombrePServicio,
              registroMarcacion.nroLegajo,
              _fechaISOFromFechaHora(registroMarcacion.fecha),
            );
          }}
        />

        <SuperaModal
          open={!!registroSupera}
          registro={registroSupera}
          onClose={() => setRegistroSupera(null)}
          onSuccess={() => {
            if (!registroSupera) return;
            marcarGestionadoLocal('SUPERA', registroSupera.nombrePServicio, registroSupera.nroLegajo, registroSupera.fecha);
          }}
        />

        {!loading && itemsOrdenados.length === 0 ? (
          <IonList>
            <IonItem lines="none">
              <IonText color="medium">Sin registros en el rango.</IonText>
            </IonItem>
          </IonList>
        ) : null}
      </IonContent>
    </IonPage>
  );
};

export default GestionErroresDetalle;
